<template>
  <b-modal v-model="show" size="xl" hide-footer no-close-on-backdrop no-enforce-focus
           dialog-class="kyc-detail-dialog"
           @hidden="onHidden">
    <template #modal-title>
      <span v-if="request">
        {{ $t('kycRequest.title') }}
        <span v-if="request.client" class="ml-2 text-muted">— {{ request.client.companyName || request.client.domain }}</span>
      </span>
      <span v-else>{{ $t('kycRequest.title') }}</span>
    </template>

    <div v-if="!request" class="text-center py-5">
      <b-spinner></b-spinner>
    </div>
    <div v-else>
      <!-- 1. WORKFLOW STEPPER -->
      <div class="workflow-stepper mb-3">
        <div v-for="(step, idx) in workflowSteps" :key="step.key" class="workflow-step"
             :class="{
               'workflow-step--done': step.done,
               'workflow-step--active': step.active,
               'workflow-step--canceled': isCanceled,
             }">
          <div class="workflow-step__dot">
            <font-awesome-icon v-if="step.done" icon="check" />
            <font-awesome-icon v-else-if="step.active && isCanceled" icon="times" />
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <div class="workflow-step__label">{{ step.label }}</div>
          <div v-if="idx < workflowSteps.length - 1" class="workflow-step__line"
               :class="{'workflow-step__line--done': step.done}"></div>
        </div>
      </div>

      <!-- 2. NEXT ACTION CARD (prominent, contextual) -->
      <div v-if="nextActionInfo" class="next-action-card mb-3" :class="'next-action-card--' + nextActionInfo.tone">
        <div class="d-flex align-items-center">
          <div class="next-action-card__icon">
            <font-awesome-icon :icon="nextActionInfo.icon" size="lg" />
          </div>
          <div class="flex-grow-1">
            <div class="next-action-card__title">{{ nextActionInfo.title }}</div>
            <div class="next-action-card__desc small">{{ nextActionInfo.desc }}</div>
          </div>
          <div v-if="nextActionInfo.button" class="ml-3">
            <button v-if="nextActionInfo.button === 'take'" @click="act('take')"
                    :disabled="acting" class="btn btn-primary">
              <font-awesome-icon icon="hand-paper" />&nbsp;{{ $t('kycRequest.action.take') }}
            </button>
            <button v-else-if="nextActionInfo.button === 'syncKyc'" @click="syncKyc"
                    :disabled="acting" class="btn btn-outline-primary is-border-1">
              <font-awesome-icon icon="sync" />&nbsp;{{ $t('kycRequest.action.syncKyc') }}
            </button>
            <button v-else-if="nextActionInfo.button === 'startReview'" @click="act('start-review')"
                    :disabled="acting" class="btn btn-primary">
              <font-awesome-icon icon="eye" />&nbsp;{{ $t('kycRequest.action.startReview') }}
            </button>
            <button v-else-if="nextActionInfo.button === 'exportContract'" @click="openExportContract"
                    :disabled="acting" class="btn btn-success">
              <font-awesome-icon icon="file-pdf" />&nbsp;{{ $t('kycRequest.action.exportContract') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 3. INFO 2 COLUMNS -->
      <div class="row">
        <!-- Khách hàng -->
        <div class="col-md-6">
          <div class="info-tile">
            <h6 class="info-tile__title">
              <font-awesome-icon icon="building" />&nbsp;Khách hàng
            </h6>
            <div v-if="request.client">
              <p class="mb-1">
                <strong>{{ request.client.companyName || request.client.domain }}</strong>
                <span v-if="request.client.isTrial" class="badge badge-warning ml-1">Trial</span>
                <span v-else-if="request.client.isPackage" class="badge badge-success ml-1">Production</span>
                <span v-if="request.client.isDemo" class="badge badge-info ml-1">Demo</span>
              </p>
              <p class="mb-1"><code class="domain-tag">{{ request.client.domain }}</code></p>
              <div class="info-row">
                <span class="info-row__lbl">Gói:</span>
                <span class="info-row__val">{{ request.client.plan ? request.client.plan.name : '—' }}</span>
              </div>
              <div class="info-row">
                <span class="info-row__lbl">User limit:</span>
                <span class="info-row__val">{{ request.client.userLimit || 0 }}</span>
              </div>
              <div class="info-row" v-if="request.client.isTrial && request.client.trialEndTime">
                <span class="info-row__lbl">Hết hạn trial:</span>
                <span class="info-row__val">{{ request.client.trialEndTime | dateOnly }}</span>
              </div>
              <div class="info-row" v-else-if="request.client.expireTime">
                <span class="info-row__lbl">Hết hạn:</span>
                <span class="info-row__val">{{ request.client.expireTime | dateOnly }}</span>
              </div>
              <div class="info-row">
                <span class="info-row__lbl">Sales phụ trách:</span>
                <span class="info-row__val">
                  <span v-if="request.client.sales">{{ request.client.sales.name }}</span>
                  <span v-else class="text-warning">Chưa gán</span>
                </span>
              </div>
              <div class="info-row">
                <span class="info-row__lbl">Account chăm sóc:</span>
                <span class="info-row__val">
                  <span v-if="request.client.account">{{ request.client.account.name }}</span>
                  <span v-else class="text-warning">Chưa gán</span>
                </span>
              </div>
              <div class="info-row" v-if="request.latestOrder">
                <span class="info-row__lbl">Đơn gần nhất:</span>
                <span class="info-row__val">
                  <strong class="text-primary">{{ formatVND(request.latestOrder.total) }}</strong>
                  <span class="text-muted small">
                    ({{ request.latestOrder.startTime | dateOnly }}<span v-if="request.latestOrder.monthCount">, {{ request.latestOrder.monthCount }}t</span>)
                  </span>
                </span>
              </div>
            </div>
            <div v-else class="text-muted">[Khách đã bị xóa]</div>
          </div>
        </div>

        <!-- Yêu cầu -->
        <div class="col-md-6">
          <div class="info-tile">
            <h6 class="info-tile__title">
              <font-awesome-icon icon="info-circle" />&nbsp;Yêu cầu
            </h6>
            <div class="info-row">
              <span class="info-row__lbl">Loại hợp đồng:</span>
              <span class="info-row__val">{{ $t('kycRequest.contractType.' + (request.contractType || 'unspecified')) }}</span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Ưu tiên:</span>
              <span class="info-row__val">
                <span :class="priorityBadgeClass(request.priority)">
                  {{ $t('kycRequest.priority.' + request.priority) }}
                </span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Tạo bởi:</span>
              <span class="info-row__val">
                {{ request.requestedBy ? request.requestedBy.name : '—' }}
                <span class="text-muted small">{{ formatDateTime(request.createTime) }}</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Account xử lý:</span>
              <span class="info-row__val">
                <span v-if="request.assignedTo">{{ request.assignedTo.name }}</span>
                <span v-else class="text-warning">Chưa gán</span>
                <button v-if="canReassign" @click="openReassign"
                        class="btn btn-link p-0 ml-2 small">
                  <font-awesome-icon icon="exchange-alt" />&nbsp;Đổi
                </button>
              </span>
            </div>
            <div v-if="request.note" class="mt-2">
              <div class="info-row__lbl mb-1">Ghi chú:</div>
              <div class="border rounded p-2 bg-light small" style="white-space: pre-wrap;">{{ request.note }}</div>
            </div>
            <div v-if="request.status === 'exported'" class="mt-2">
              <div class="info-row__lbl mb-1">Số hợp đồng:</div>
              <strong class="text-primary">{{ request.exportedContractNumber }}</strong>
              <span class="text-muted small ml-2">{{ formatDateTime(request.exportTime) }}</span>
            </div>
            <div v-if="request.status === 'canceled'" class="mt-2">
              <div class="info-row__lbl mb-1">Lý do hủy:</div>
              <div class="border rounded p-2 bg-light small text-danger" style="white-space: pre-wrap;">{{ request.cancelReason || '—' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. KYC INFO INLINE (khi đã có KYC) -->
      <div v-if="request.kyc" class="info-tile">
        <h6 class="info-tile__title d-flex justify-content-between align-items-center">
          <span>
            <font-awesome-icon icon="id-card" />&nbsp;Hồ sơ KYC khách
            <span v-if="request.kyc.type === 'enterprise'" class="badge badge-primary ml-2">Doanh nghiệp</span>
            <span v-else-if="request.kyc.type === 'personal'" class="badge badge-secondary ml-2">Cá nhân</span>
          </span>
          <button type="button" class="btn btn-sm btn-outline-info is-border-1"
                  @click="openFullKyc">
            <font-awesome-icon icon="external-link-alt" />&nbsp;Xem đầy đủ
          </button>
        </h6>

        <div class="row">
          <!-- Enterprise -->
          <div v-if="request.kyc.type === 'enterprise'" class="col-md-6">
            <div class="info-row">
              <span class="info-row__lbl">Tên công ty:</span>
              <span class="info-row__val">
                <strong v-if="request.kyc.companyName">{{ request.kyc.companyName }}</strong>
                <span v-else class="text-warning">⚠ thiếu</span>
                <span v-if="request.kyc.shortName" class="text-muted">({{ request.kyc.shortName }})</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">MST công ty:</span>
              <span class="info-row__val">
                <span v-if="request.kyc.companyTaxCode">{{ request.kyc.companyTaxCode }}</span>
                <span v-else class="text-warning">⚠ thiếu</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Địa chỉ cty:</span>
              <span class="info-row__val">
                <span v-if="request.kyc.companyAddress">{{ request.kyc.companyAddress }}</span>
                <span v-else class="text-warning">⚠ thiếu</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Người đại diện:</span>
              <span class="info-row__val">
                <strong v-if="request.kyc.name">{{ request.kyc.name }}</strong>
                <span v-else class="text-warning">⚠ thiếu</span>
                <span v-if="request.kyc.position" class="text-muted">, {{ request.kyc.position }}</span>
              </span>
            </div>
          </div>
          <!-- Personal -->
          <div v-else class="col-md-6">
            <div class="info-row">
              <span class="info-row__lbl">Họ tên:</span>
              <span class="info-row__val">
                <strong v-if="request.kyc.name">{{ request.kyc.name }}</strong>
                <span v-else class="text-warning">⚠ thiếu</span>
              </span>
            </div>
            <div class="info-row" v-if="request.kyc.taxCode">
              <span class="info-row__lbl">MST cá nhân:</span>
              <span class="info-row__val">{{ request.kyc.taxCode }}</span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Địa chỉ:</span>
              <span class="info-row__val">
                <span v-if="request.kyc.address">{{ request.kyc.address }}</span>
                <span v-else class="text-warning">⚠ thiếu</span>
              </span>
            </div>
          </div>

          <!-- Common -->
          <div class="col-md-6">
            <div class="info-row" v-if="request.kyc.signerEmail">
              <span class="info-row__lbl">Email ký HĐ:</span>
              <span class="info-row__val">{{ request.kyc.signerEmail }}</span>
            </div>
            <div class="info-row" v-if="request.kyc.invoiceEmails && request.kyc.invoiceEmails.length">
              <span class="info-row__lbl">Email nhận HĐ:</span>
              <span class="info-row__val">{{ request.kyc.invoiceEmails.join(', ') }}</span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Xuất hoá đơn:</span>
              <span class="info-row__val">
                {{ request.kyc.invoiceEnabled ? 'Có' : 'Không' }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-row__lbl">Đã nộp lúc:</span>
              <span class="info-row__val">{{ formatDateTime(request.kyc.createTime) }}</span>
            </div>
          </div>
        </div>

        <!-- Ảnh KYC thumbnails -->
        <div class="kyc-images mt-3">
          <div class="info-row__lbl mb-2">Ảnh KYC (click để xem to):</div>
          <div class="d-flex flex-wrap">
            <kyc-image-thumb v-if="request.kyc.IDFront" :hash="request.kyc.IDFront" label="CCCD trước" />
            <div v-else class="kyc-thumb kyc-thumb--missing">
              <font-awesome-icon icon="exclamation-triangle" />
              <div class="small">⚠ CCCD trước</div>
            </div>
            <kyc-image-thumb v-if="request.kyc.IDBack" :hash="request.kyc.IDBack" label="CCCD sau" />
            <div v-else class="kyc-thumb kyc-thumb--missing">
              <font-awesome-icon icon="exclamation-triangle" />
              <div class="small">⚠ CCCD sau</div>
            </div>
            <kyc-image-thumb v-if="request.kyc.owner" :hash="request.kyc.owner" label="Người đại diện" />
            <div v-else class="kyc-thumb kyc-thumb--missing">
              <font-awesome-icon icon="exclamation-triangle" />
              <div class="small">⚠ Người đại diện</div>
            </div>
            <template v-if="request.kyc.type === 'enterprise'">
              <kyc-image-thumb v-if="request.kyc.businessRegistration" :hash="request.kyc.businessRegistration" label="ĐKKD" />
              <div v-else class="kyc-thumb kyc-thumb--missing">
                <font-awesome-icon icon="exclamation-triangle" />
                <div class="small">⚠ ĐKKD</div>
              </div>
            </template>
            <template v-else-if="request.kyc.type === 'personal'">
              <kyc-image-thumb v-if="request.kyc.proofOfAddress" :hash="request.kyc.proofOfAddress" label="Xác minh địa chỉ" />
              <div v-else class="kyc-thumb kyc-thumb--missing">
                <font-awesome-icon icon="exclamation-triangle" />
                <div class="small">⚠ Xác minh địa chỉ</div>
              </div>
            </template>
          </div>
        </div>

        <div v-if="kycMissingFields.length" class="alert alert-warning mt-2 mb-0 py-2">
          <font-awesome-icon icon="exclamation-triangle" />
          <strong class="ml-2">Thiếu {{ kycMissingFields.length }} trường:</strong>
          <span class="small">{{ kycMissingFields.join(', ') }}</span>
        </div>
      </div>

      <!-- 4b. MISA WeSign — sau bước "Đã xuất hợp đồng" -->
      <div v-if="request.kyc && request.status === 'exported'" class="info-tile">
        <h6 class="info-tile__title d-flex justify-content-between align-items-center">
          <span>
            <font-awesome-icon icon="signature" />&nbsp;Hợp đồng MISA WeSign
            <span v-if="request.kyc.wesignStatus" class="badge ml-2" :class="wesignBadgeClass(request.kyc.wesignStatus)">
              {{ wesignStatusLabel(request.kyc.wesignStatus) }}
            </span>
          </span>
          <span v-if="request.kyc.wesignAmisappRef" class="small text-muted">
            <span :class="customerSignedCount(request.kyc) === customerParticipants(request.kyc).length ? 'text-success' : 'text-warning'">
              <strong>Khách: {{ customerSignedCount(request.kyc) }}/{{ customerParticipants(request.kyc).length }}</strong>
            </span> đã ký
          </span>
        </h6>

        <!-- Đã liên kết: hiển thị box chi tiết -->
        <div v-if="request.kyc.wesignAmisappRef">
          <div v-if="request.kyc.wesignDocumentName" class="mb-2">
            <font-awesome-icon icon="file-signature" class="text-muted mr-1"/>
            <strong>{{ request.kyc.wesignDocumentName }}</strong>
          </div>
          <table v-if="request.kyc.wesignParticipants && request.kyc.wesignParticipants.length"
                 class="table table-sm table-bordered small mb-2 bg-white">
            <thead class="thead-light">
              <tr><th>#</th><th>Người ký</th><th>Vai trò</th><th>Email</th><th>Trạng thái</th></tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in request.kyc.wesignParticipants" :key="idx"
                  :class="{'table-light': isInternalParticipant(p)}">
                <td>{{ p.priority || idx + 1 }}</td>
                <td>{{ p.name }}</td>
                <td>
                  <span class="badge" :class="isInternalParticipant(p) ? 'badge-secondary' : 'badge-info'">
                    {{ isInternalParticipant(p) ? 'Nội bộ' : 'Khách' }}
                  </span>
                </td>
                <td>{{ p.email }}</td>
                <td>
                  <span :class="customerActionClass(p.action)">
                    {{ customerActionLabel(p.action) }}
                  </span>
                  <span v-if="p.signTime" class="text-muted small ml-1">
                    ({{ p.signTime | moment('DD/MM HH:mm') }})
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex justify-content-between align-items-center small text-muted">
            <div>
              <code>{{ request.kyc.wesignAmisappRef }}</code>
              <span v-if="request.kyc.wesignLastSyncTime" class="ml-2">
                <font-awesome-icon icon="sync"/>&nbsp;Đồng bộ {{ formatTimeAgoShort(request.kyc.wesignLastSyncTime) }}
              </span>
              <span v-else class="ml-2 text-warning">
                <font-awesome-icon icon="exclamation-triangle"/>&nbsp;Chưa đồng bộ
              </span>
            </div>
            <div>
              <a :href="'https://amisapp.misa.vn/wesign/documents/waiting-for-others/' + request.kyc.wesignAmisappRef"
                 target="_blank" class="btn btn-sm btn-outline-info is-border-1 mr-1">
                <font-awesome-icon icon="external-link-alt"/>&nbsp;Mở MISA
              </a>
              <button class="btn btn-sm btn-outline-primary is-border-1 mr-1"
                      @click="onSyncWesign" :disabled="wesignBusy">
                <font-awesome-icon icon="sync" :spin="wesignBusy === 'sync'"/>&nbsp;Đồng bộ
              </button>
              <button class="btn btn-sm btn-outline-danger is-border-1"
                      @click="onUnlinkWesign" :disabled="wesignBusy">
                <font-awesome-icon icon="unlink"/>&nbsp;Bỏ liên kết
              </button>
            </div>
          </div>
        </div>

        <!-- Chưa liên kết: form paste UUID -->
        <div v-else>
          <p class="small text-muted mb-2">
            Paste mã hợp đồng MISA (UUID) hoặc URL từ dashboard MISA. Worker sẽ tự đồng bộ trạng thái mỗi 30 phút.
          </p>
          <div class="input-group">
            <input class="form-control form-control-sm" v-model="wesignRefInput"
                   placeholder="Paste UUID hoặc URL MISA (vd https://amisapp.misa.vn/wesign/documents/.../ce886d2d-…)"
                   :disabled="wesignBusy"/>
            <div class="input-group-append">
              <button class="btn btn-sm btn-outline-primary is-border-1" @click="onLinkWesign"
                      :disabled="wesignBusy || !wesignRefInput || !wesignRefInput.trim()">
                <font-awesome-icon icon="link" :spin="wesignBusy === 'link'"/>&nbsp;Liên kết
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 5. HISTORY -->
      <div class="info-tile">
        <h6 class="info-tile__title">
          <font-awesome-icon icon="history" />&nbsp;Lịch sử
        </h6>
        <ul class="list-unstyled timeline mb-0">
          <li v-for="(h, idx) in historyReversed" :key="idx" class="timeline__item">
            <k-y-c-request-status-badge :status="h.status" />
            <span class="ml-2 text-muted small">{{ formatDateTime(h.at) }}</span>
            <span class="ml-2 small"><strong>{{ h.by ? h.by.name : $t('kycRequest.history.bySystem') }}</strong></span>
            <div v-if="h.note" class="text-muted small mt-1" style="padding-left: 1rem; white-space: pre-wrap;">{{ h.note }}</div>
          </li>
        </ul>
      </div>

      <!-- 6. FOOTER ACTIONS (secondary) -->
      <div class="detail-footer">
        <button v-if="canRollback"
                @click="rollback"
                :disabled="acting"
                class="btn btn-outline-warning is-border-1 mr-2"
                title="Quay lại trạng thái trước nếu bấm nhầm">
          <font-awesome-icon icon="undo" />&nbsp;Quay lại bước trước
        </button>
        <div class="flex-grow-1"></div>
        <button v-if="canCancel"
                @click="openCancel"
                :disabled="acting"
                class="btn btn-outline-danger is-border-1">
          <font-awesome-icon icon="times-circle" />&nbsp;{{ $t('kycRequest.action.cancel') }}
        </button>
        <button @click="show = false" class="btn btn-secondary ml-2">
          Đóng
        </button>
      </div>
    </div>

    <!-- Reassign -->
    <b-modal v-model="reassignShow" :title="$t('kycRequest.action.reassign')" hide-footer no-enforce-focus>
      <b-form @submit.prevent="doReassign">
        <b-form-group :label="$t('kycRequest.field.assignedTo')">
          <admin-user-select :model="reassignForm" attribute="assignedTo"
                             :place-holder="$t('kycRequest.field.assignedTo')" />
        </b-form-group>
        <b-form-group label="Lý do">
          <b-form-input v-model="reassignForm.reason" maxlength="500" />
        </b-form-group>
        <div class="text-right">
          <b-button @click="reassignShow = false" variant="outline-secondary" class="is-border-1 mr-2">
            {{ $t('cancel') }}
          </b-button>
          <b-button type="submit" variant="primary" :disabled="!reassignForm.assignedTo">
            {{ $t('update') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Cancel -->
    <b-modal v-model="cancelShow" :title="$t('kycRequest.action.cancel')" hide-footer no-enforce-focus>
      <b-form @submit.prevent="doCancel">
        <b-form-group :label="$t('kycRequest.field.cancelReason') + ' *'">
          <b-form-textarea v-model="cancelForm.reason" rows="3" maxlength="500" required />
        </b-form-group>
        <div class="text-right">
          <b-button @click="cancelShow = false" variant="outline-secondary" class="is-border-1 mr-2">
            {{ $t('cancel') }}
          </b-button>
          <b-button type="submit" variant="danger" :disabled="!cancelForm.reason.trim()">
            {{ $t('kycRequest.action.cancel') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Export Contract -->
    <export-contract-modal ref="exportContract"
                           :kyc-request-id="request ? request._id : null"
                           @exported="onExported" />

    <!-- Full KYC Form modal (xem chi tiết đầy đủ, kể cả ảnh + review) -->
    <k-y-c-form ref="kycForm" />
  </b-modal>
</template>

<script>
import KYCRequestStatusBadge from './KYCRequestStatusBadge.vue';
import ExportContractModal from '../client-kyc/ExportContractModal.vue';
import KYCForm from '../client-kyc/KYCForm.vue';
import KycImageThumb from './KycImageThumb.vue';
import AdminUserSelect from '@/components/AdminUserSelect.vue';
import moment from 'moment';

// Role có quyền act-all (không cần là assignedTo): admin/owner + nhóm process (account, accountant, am, manager, supervisor).
const ADMIN_ROLES = ['owner','admin','tech','operationsManager','account','accountant','am','manager','supervisor'];

// Thứ tự workflow chính
const WORKFLOW = ['pending','waitingKyc','kycSubmitted','inReview','exported','wesign'];

export default {
  components: { KYCRequestStatusBadge, ExportContractModal, AdminUserSelect, KYCForm, KycImageThumb },
  data() {
    return {
      show: false,
      currentId: null,
      request: null,
      acting: false,
      reassignShow: false,
      reassignForm: { assignedTo: null, reason: '' },
      cancelShow: false,
      cancelForm: { reason: '' },
      wesignRefInput: '',
      wesignBusy: false,  // false | 'link' | 'sync' | 'unlink'
    };
  },
  filters: {
    dateOnly(v) {
      if (!v) return '';
      return moment(parseInt(v)).format('DD/MM/YYYY');
    },
  },
  computed: {
    userRole() {
      const u = this.$store.state.user;
      return u ? u.role : null;
    },
    userId() {
      const u = this.$store.state.user;
      return u ? u._id : null;
    },
    isCreator() {
      if (!this.request || !this.request.requestedBy || !this.userId) return false;
      const reqById = (this.request.requestedBy._id || this.request.requestedBy).toString();
      return reqById === this.userId.toString();
    },
    isAssignedTo() {
      if (!this.request || !this.request.assignedTo || !this.userId) return false;
      const aid = (this.request.assignedTo._id || this.request.assignedTo).toString();
      return aid === this.userId.toString();
    },
    isAdminRole() {
      return ADMIN_ROLES.indexOf(this.userRole) >= 0;
    },
    canAct() {
      // Quyền xử lý chính: assignedTo HOẶC admin/owner
      return this.isAdminRole || this.isAssignedTo;
    },
    requestStatus() {
      return this.request ? this.request.status : null;
    },
    isCanceled() { return this.requestStatus === 'canceled'; },
    canTake() { return this.canAct; },
    canStartReview() { return this.canAct; },
    canSyncKyc() { return this.canAct; },
    canReassign() {
      return this.canAct
        && ['exported','canceled'].indexOf(this.requestStatus) < 0
        && !!this.request;
    },
    canExportContract() {
      return this.canAct && this.requestStatus === 'inReview';
    },
    canCancel() {
      if (!this.request) return false;
      if (['exported','canceled'].indexOf(this.requestStatus) >= 0) return false;
      // Người tạo HOẶC người được bổ nhiệm HOẶC admin/owner
      return this.isCreator || this.isAssignedTo || this.isAdminRole;
    },
    historyReversed() {
      if (!this.request || !this.request.history) return [];
      return this.request.history.slice().reverse();
    },
    kycMissingFields() {
      const kyc = this.request && this.request.kyc;
      if (!kyc) return [];
      const missing = [];
      if (!kyc.type) missing.push('Loại');
      if (!kyc.IDFront) missing.push('CCCD trước');
      if (!kyc.IDBack) missing.push('CCCD sau');
      if (!kyc.owner) missing.push('Ảnh người đại diện');
      if (!kyc.name) missing.push('Họ tên');
      if (!kyc.address) missing.push('Địa chỉ');
      if (kyc.type === 'enterprise') {
        if (!kyc.businessRegistration) missing.push('Ảnh ĐKKD');
        if (!kyc.companyName) missing.push('Tên cty');
        if (!kyc.companyTaxCode) missing.push('MST cty');
        if (!kyc.companyAddress) missing.push('Địa chỉ cty');
        if (!kyc.position) missing.push('Chức vụ');
      }
      if (kyc.invoiceEnabled && (!kyc.invoiceEmails || !kyc.invoiceEmails.length)) {
        missing.push('Email nhận HĐ');
      }
      return missing;
    },
    canRollback() {
      // Cho phép rollback từ waitingKyc / kycSubmitted / inReview (1 bước trước)
      return ['waitingKyc','kycSubmitted','inReview'].indexOf(this.requestStatus) >= 0
        && this.canAct;
    },
    workflowSteps() {
      const labels = {
        pending: 'Mới',
        waitingKyc: 'Chờ KYC',
        kycSubmitted: 'Đã nộp KYC',
        inReview: 'Đang duyệt',
        exported: 'Đã xuất HĐ',
        wesign: 'Ký MISA',
      };
      // Status 'wesign' không phải là KYCRequest.status thật — derive từ KYC link MISA
      const requestStatus = this.requestStatus || 'pending';
      let status = this.isCanceled ? 'pending' : requestStatus;
      // Nếu đã exported và KYC có link MISA → coi như tiến vào step 'wesign'
      if (status === 'exported' && this.request && this.request.kyc && this.request.kyc.wesignAmisappRef) {
        status = 'wesign';
      }
      const idx = WORKFLOW.indexOf(status);
      // Done logic: nếu wesign status=completed → 'wesign' done
      const wesignCompleted = this.request && this.request.kyc && this.request.kyc.wesignStatus === 'completed';
      return WORKFLOW.map((key, i) => ({
        key,
        label: labels[key],
        done: !this.isCanceled && (i < idx || (key === 'wesign' && wesignCompleted)),
        active: i === idx && !(key === 'wesign' && wesignCompleted),
      }));
    },
    nextActionInfo() {
      if (!this.request) return null;
      const s = this.requestStatus;
      if (s === 'pending' && this.canTake) {
        // Đã là assignedTo (đã được giao) → không cần click "Tiếp nhận" nữa; modal sẽ auto-take.
        if (this.isAssignedTo) {
          return {
            icon: 'hand-paper',
            tone: 'info',
            title: 'Bạn đang được giao xử lý hồ sơ này',
            desc: 'Hệ thống đang tự động tiếp nhận, vui lòng đợi giây lát.',
            button: null,
          };
        }
        return {
          icon: 'hand-paper',
          tone: 'primary',
          title: 'Bước tiếp: Tiếp nhận yêu cầu',
          desc: 'Bấm "Tiếp nhận" để gán bạn làm Account xử lý hồ sơ này.',
          button: 'take',
        };
      }
      if (s === 'waitingKyc') {
        return {
          icon: 'hourglass-half',
          tone: 'warning',
          title: 'Đang chờ khách nộp KYC',
          desc: 'Khách hàng đã thấy thông báo trên callio-client. Hệ thống sẽ tự động chuyển trạng thái khi khách nộp. Bấm "Đồng bộ thủ công" nếu cần ép kiểm tra ngay.',
          button: this.canSyncKyc ? 'syncKyc' : null,
        };
      }
      if (s === 'kycSubmitted' && this.canStartReview) {
        return {
          icon: 'eye',
          tone: 'info',
          title: 'Bước tiếp: Xem xét hồ sơ KYC',
          desc: 'Vui lòng kiểm tra thông tin pháp lý phía dưới. Nếu đầy đủ và chính xác, bấm "Bắt đầu duyệt" để khoá hồ sơ và chuyển sang bước xuất hợp đồng.',
          button: 'startReview',
        };
      }
      if (s === 'inReview' && this.canExportContract) {
        return {
          icon: 'file-pdf',
          tone: 'success',
          title: 'Bước tiếp: Xuất hợp đồng PDF',
          desc: 'Bấm "Xuất hợp đồng" để chọn mẫu, nhập số HĐ và tải về PDF gửi khách ký.',
          button: 'exportContract',
        };
      }
      if (s === 'exported') {
        return {
          icon: 'check-circle',
          tone: 'success',
          title: 'Đã hoàn tất',
          desc: 'Hợp đồng đã được xuất thành công. Số HĐ: ' + (this.request.exportedContractNumber || '—'),
          button: null,
        };
      }
      if (s === 'canceled') {
        return {
          icon: 'times-circle',
          tone: 'danger',
          title: 'Yêu cầu đã bị hủy',
          desc: 'Lý do: ' + (this.request.cancelReason || '—'),
          button: null,
        };
      }
      return null;
    },
  },
  methods: {
    // ===== MISA WeSign helpers =====
    wesignStatusLabel(s) {
      const m = {pending: 'Chờ ký', inProgress: 'Đang ký', completed: 'Đã ký xong', rejected: 'Khách từ chối', cancelled: 'Đã huỷ', expired: 'Hết hạn'};
      return m[s] || s;
    },
    wesignBadgeClass(s) {
      const m = {pending: 'badge-secondary', inProgress: 'badge-info', completed: 'badge-success', rejected: 'badge-danger', cancelled: 'badge-warning', expired: 'badge-dark'};
      return m[s] || 'badge-light';
    },
    isInternalParticipant(p) {
      return !!(p && p.email && p.email.toLowerCase().includes('@gadget.com.vn'));
    },
    customerParticipants(kyc) {
      return (kyc && kyc.wesignParticipants || []).filter(p => !this.isInternalParticipant(p));
    },
    customerSignedCount(kyc) {
      return this.customerParticipants(kyc).filter(p => p.action === 2).length;
    },
    customerActionLabel(action) {
      const m = {0: '⏳ Chưa nhận', 1: '⏳ Đang chờ ký', 2: '✓ Đã ký', 3: '✗ Từ chối'};
      return m[action] || '?';
    },
    customerActionClass(action) {
      if (action === 2) return 'text-success';
      if (action === 3) return 'text-danger';
      if (action === 1) return 'text-info';
      return 'text-muted';
    },
    formatTimeAgoShort(t) {
      if (!t) return '';
      const min = Math.floor(moment().diff(moment(t)) / 60000);
      if (min < 1) return 'vừa xong';
      if (min < 60) return min + 'p trước';
      const h = Math.floor(min / 60);
      if (h < 24) return h + 'h trước';
      return Math.floor(h / 24) + ' ngày trước';
    },
    async onLinkWesign() {
      const raw = (this.wesignRefInput || '').trim();
      if (!raw || !this.request.kyc) return;
      this.wesignBusy = 'link';
      try {
        const res = await this.$service.post(`/client-kyc/${this.request.kyc._id}/link-wesign-ref`,
            {wesignAmisappRef: raw});
        if (res.data && res.data.success) {
          this.$set(this.request.kyc, 'wesignAmisappRef', res.data.wesignAmisappRef);
          this.$set(this.request.kyc, 'wesignStatus', res.data.wesignStatus);
          this.$set(this.request.kyc, 'wesignDocumentName', res.data.wesignDocumentName);
          this.$set(this.request.kyc, 'wesignParticipants', res.data.wesignParticipants || []);
          this.$set(this.request.kyc, 'wesignLinkTime', Date.now());
          this.$set(this.request.kyc, 'wesignLastSyncTime', Date.now());
          this.wesignRefInput = '';
          this.$swal({title: 'Đã liên kết MISA', icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false});
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Liên kết thất bại', icon: 'error'});
      } finally {
        this.wesignBusy = false;
      }
    },
    async onSyncWesign() {
      if (!this.request.kyc) return;
      this.wesignBusy = 'sync';
      try {
        const res = await this.$service.post(`/client-kyc/${this.request.kyc._id}/sync-wesign-ref`);
        if (res.data && res.data.success) {
          this.$set(this.request.kyc, 'wesignStatus', res.data.wesignStatus);
          this.$set(this.request.kyc, 'wesignDocumentName', res.data.wesignDocumentName);
          this.$set(this.request.kyc, 'wesignParticipants', res.data.wesignParticipants || []);
          this.$set(this.request.kyc, 'wesignLastSyncTime', Date.now());
          this.$swal({title: 'Đã đồng bộ', icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Đồng bộ thất bại', icon: 'error'});
      } finally {
        this.wesignBusy = false;
      }
    },
    async onUnlinkWesign() {
      if (!this.request.kyc) return;
      const ok = await this.$swal({
        title: 'Bỏ liên kết MISA?',
        text: 'Xoá mã hợp đồng + lịch sử thông báo. Không ảnh hưởng dữ liệu phía MISA.',
        icon: 'warning', showCancelButton: true,
      });
      if (!ok.value) return;
      this.wesignBusy = 'unlink';
      try {
        await this.$service.delete(`/client-kyc/${this.request.kyc._id}/wesign-ref`);
        this.$set(this.request.kyc, 'wesignAmisappRef', null);
        this.$set(this.request.kyc, 'wesignStatus', null);
        this.$set(this.request.kyc, 'wesignDocumentName', null);
        this.$set(this.request.kyc, 'wesignParticipants', []);
        this.$swal({title: 'Đã bỏ liên kết', icon: 'success', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false});
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Bỏ liên kết thất bại', icon: 'error'});
      } finally {
        this.wesignBusy = false;
      }
    },
    open(id) {
      this.show = true;
      this.currentId = id;
      this.request = null;
      this.wesignRefInput = '';
      this.wesignBusy = false;
      this.load();
    },
    async load() {
      try {
        const resp = await this.$service.get('/kyc-request/' + this.currentId);
        this.request = resp.data;
        // Nếu user đang là assignedTo và hồ sơ vẫn ở pending → auto-tiếp nhận để chuyển status.
        if (this.request && this.request.status === 'pending' && this.isAssignedTo) {
          this.autoTake();
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Load failed';
        this.$swal({text: msg, icon: 'error'});
        this.show = false;
      }
    },
    async autoTake() {
      try {
        const resp = await this.$service.post('/kyc-request/' + this.currentId + '/take');
        this.request = resp.data;
        this.$emit('updated');
      } catch (e) {
        // Silent — có thể request đã được người khác chuyển status, hoặc network lỗi. User sẽ thấy state mới khi reload.
      }
    },
    async refreshRequest() {
      const resp = await this.$service.get('/kyc-request/' + this.currentId);
      return resp.data;
    },
    async act(action) {
      this.acting = true;
      try {
        await this.$service.post('/kyc-request/' + this.currentId + '/' + action);
        this.request = await this.refreshRequest();
        this.$emit('updated');
        this.$swal({text: this.$t('success'), icon: 'success', timer: 1500});
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Action failed';
        this.$swal({text: msg, icon: 'error'});
      } finally {
        this.acting = false;
      }
    },
    async rollback() {
      const confirm = await this.$swal({
        text: 'Quay lại trạng thái trước? Hành động này sẽ ghi vào lịch sử.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Quay lại',
        cancelButtonText: 'Hủy',
      });
      if (!confirm.value) return;
      this.acting = true;
      try {
        await this.$service.post('/kyc-request/' + this.currentId + '/rollback');
        this.request = await this.refreshRequest();
        this.$emit('updated');
        this.$swal({text: this.$t('success'), icon: 'success', timer: 1500});
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Rollback failed';
        this.$swal({text: msg, icon: 'error'});
      } finally {
        this.acting = false;
      }
    },
    async syncKyc() {
      this.acting = true;
      try {
        await this.$service.post('/kyc-request/' + this.currentId + '/sync-kyc');
        this.request = await this.refreshRequest();
        this.$emit('updated');
        this.$swal({text: this.$t('success'), icon: 'success', timer: 1500});
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Sync failed';
        this.$swal({text: msg, icon: 'error'});
      } finally {
        this.acting = false;
      }
    },
    openReassign() {
      const cur = this.request && this.request.assignedTo ? (this.request.assignedTo._id || this.request.assignedTo) : null;
      this.reassignForm = {assignedTo: cur, reason: ''};
      this.reassignShow = true;
    },
    async doReassign() {
      try {
        await this.$service.post('/kyc-request/' + this.currentId + '/reassign', this.reassignForm);
        this.request = await this.refreshRequest();
        this.reassignShow = false;
        this.$emit('updated');
        this.$swal({text: this.$t('success'), icon: 'success', timer: 1500});
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Reassign failed';
        this.$swal({text: msg, icon: 'error'});
      }
    },
    openCancel() {
      this.cancelForm = {reason: ''};
      this.cancelShow = true;
    },
    async doCancel() {
      try {
        await this.$service.post('/kyc-request/' + this.currentId + '/cancel', this.cancelForm);
        this.request = await this.refreshRequest();
        this.cancelShow = false;
        this.$emit('updated');
        this.$swal({text: this.$t('success'), icon: 'success', timer: 1500});
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Cancel failed';
        this.$swal({text: msg, icon: 'error'});
      }
    },
    openExportContract() {
      const kycId = this.request && this.request.kyc && this.request.kyc._id;
      if (!kycId) {
        this.$swal({text: 'Khách chưa nộp KYC', icon: 'warning'});
        return;
      }
      this.$refs.exportContract.open(kycId);
    },
    openFullKyc() {
      const kycId = this.request && this.request.kyc && this.request.kyc._id;
      if (!kycId) {
        this.$swal({text: 'Khách chưa nộp KYC', icon: 'warning'});
        return;
      }
      this.$refs.kycForm.update(kycId);
    },
    async onExported() {
      this.request = await this.refreshRequest();
      this.$emit('updated');
    },
    onHidden() {
      this.currentId = null;
      this.request = null;
    },
    formatDateTime(t) {
      return t ? moment(parseInt(t)).format('DD/MM/YYYY HH:mm') : '';
    },
    priorityBadgeClass(p) {
      const map = {normal: 'badge badge-light', high: 'badge badge-warning', urgent: 'badge badge-danger'};
      return map[p] || 'badge badge-light';
    },
    formatVND(num) {
      if (num == null || isNaN(num)) return '';
      return Number(num).toLocaleString('vi-VN') + ' đ';
    },
  },
};
</script>

<style scoped>
/* Stepper */
.workflow-stepper {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  background: #f8f9fc;
  border-radius: 6px;
}
.workflow-step {
  flex: 1;
  position: relative;
  text-align: center;
  padding: 0 4px;
}
.workflow-step__dot {
  width: 32px; height: 32px; border-radius: 50%;
  background: #dee2e6; color: #6c757d;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 4px;
  font-weight: 600; font-size: 13px;
  position: relative; z-index: 2;
  border: 2px solid #fff;
}
.workflow-step__label {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.3;
}
.workflow-step__line {
  position: absolute;
  top: 16px;
  left: 50%;
  right: -50%;
  height: 2px;
  background: #dee2e6;
  z-index: 1;
}
.workflow-step__line--done { background: #28a745; }
.workflow-step--done .workflow-step__dot { background: #28a745; color: #fff; }
.workflow-step--done .workflow-step__label { color: #28a745; font-weight: 500; }
.workflow-step--active .workflow-step__dot { background: #1e90ff; color: #fff; box-shadow: 0 0 0 4px rgba(30,144,255,0.18); }
.workflow-step--active .workflow-step__label { color: #1e90ff; font-weight: 600; }
.workflow-step--canceled.workflow-step--active .workflow-step__dot { background: #dc3545; box-shadow: 0 0 0 4px rgba(220,53,69,0.18); }
.workflow-step--canceled.workflow-step--active .workflow-step__label { color: #dc3545; }

/* Next action */
.next-action-card {
  padding: 14px 18px;
  border-radius: 8px;
  border-left: 4px solid;
}
.next-action-card--primary { background: #e7f1ff; border-color: #1e90ff; color: #0d4f9e; }
.next-action-card--info { background: #e7f7fa; border-color: #17a2b8; color: #0c5460; }
.next-action-card--warning { background: #fff8e1; border-color: #ffb74d; color: #856404; }
.next-action-card--success { background: #e6f7ee; border-color: #28a745; color: #1e7e34; }
.next-action-card--danger { background: #fdecea; border-color: #dc3545; color: #721c24; }
.next-action-card__icon {
  width: 44px; height: 44px; border-radius: 50%;
  background: rgba(255,255,255,0.6);
  display: flex; align-items: center; justify-content: center;
  margin-right: 12px;
}
.next-action-card__title { font-weight: 600; font-size: 15px; }
.next-action-card__desc { opacity: 0.85; }

/* Info tiles */
.info-tile {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  height: calc(100% - 12px);
}
.info-tile__title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f3f5;
  color: #2c3e50;
}
.info-row {
  font-size: 13px;
  line-height: 1.8;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.info-row__lbl { color: #6c757d; min-width: 130px; }
.info-row__val { flex: 1; }
.domain-tag {
  background: #f1f3f5; color: #495057; padding: 2px 6px;
  border-radius: 3px; font-size: 12px;
}

/* History timeline */
.timeline__item {
  padding: 8px 0;
  border-bottom: 1px dashed #f1f3f5;
}
.timeline__item:last-child { border-bottom: 0; }

/* KYC missing placeholder */
.kyc-thumb--missing {
  width: 120px; height: 100px;
  border: 1px dashed #ffc107; border-radius: 6px;
  margin-right: 8px; margin-bottom: 8px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #ffc107;
  background: #fffbeb;
}
.kyc-thumb--missing .small { font-size: 11px; margin-top: 4px; }

/* Footer */
.detail-footer {
  display: flex;
  align-items: center;
  padding: 12px 0 0;
  border-top: 1px solid #e9ecef;
  margin-top: 8px;
}
</style>
